import type { AxisDataService, AxisBoundaries, FetchResult, ExpandResult } from "@o2d/datasource";
import { container } from "tsyringe";
import { AxisDataClient } from "../connectivity/axis-data-client";
import type { QueryParameter } from '../connectivity/query-parameter.interface';
import type { SortConfig } from '../connectivity/sort-config.types';
import { TimelineLoadTracker } from "./timeline-load-tracker.service";

/**
 * Configuration options for BackendAxisDataService.
 * Used by the AxisDataProviderFactory to instantiate the service.
 */
export interface BackendAxisDataServiceOptions {
    /** Entity type to query (e.g., 'Employee', 'Project') */
    entityType: string;
    /** Sort configuration (strategy, path, direction) */
    sort: SortConfig;
    /** Optional query parameters for filtering entities on the Y-axis */
    queryParams?: QueryParameter<unknown>[];
    /** Callback fired once after first successful Y-axis data fetch. */
    onFirstFetch?: (firstShortId: string) => void;
}

/**
 * Backend-backed implementation of AxisDataService.
 * Fetches entity axis data from the backend API with cursor-based pagination.
 *
 * This service translates between the AxisDataService interface (used by @o2d/datasource)
 * and the backend axis-data API endpoint.
 *
 * Constructor signature matches AxisDataProviderFactory expectations:
 * (dataServiceId: string, options: TOptions)
 */
/**
 * Metadata for axis entities (title, color).
 * Populated by BackendAxisDataService during fetch/expand operations.
 * Consumed by header data providers to display entity names and colors
 * without redundant API calls.
 */
export type AxisEntityMetadata = { title: string; color: string | null };

/**
 * Global store for axis entity metadata keyed by shortId.
 * Shared across all BackendAxisDataService instances.
 */
export const axisEntityMetadata = new Map<string, AxisEntityMetadata>();

export class BackendAxisDataService implements AxisDataService<string> {

    private cachedBoundaries: AxisBoundaries | null = null;
    private readonly _axisDataServiceId: string;
    private readonly axisDataClient: AxisDataClient;
    /** Counts this service's requests for the boards' loading bar. */
    private readonly loadTracker: TimelineLoadTracker;
    private readonly options: BackendAxisDataServiceOptions;
    private _hasCalledFirstFetch = false;

    /**
     * Creates a new BackendAxisDataService instance.
     * Constructor signature matches AxisDataProviderFactory pattern.
     *
     * @param axisDataServiceId - Unique identifier for this service instance
     * @param options - Configuration options for this service
     */
    constructor(
        axisDataServiceId: string,
        options: BackendAxisDataServiceOptions
    ) {
        this._axisDataServiceId = axisDataServiceId;
        this.options = options;
        this.axisDataClient = container.resolve(AxisDataClient);
        this.loadTracker = container.resolve(TimelineLoadTracker);
    }

    /**
     * Gets the unique identifier for this axis data service instance.
     */
    public get axisDataServiceId(): string {
        return this._axisDataServiceId;
    }

    /**
     * Fetches values from the backend with cursor-based pagination.
     * Empty string is mapped to null (= start from first entity).
     * Automatically retries with referenceUid null on 404 (deleted entity).
     *
     * @param referenceValue - The reference UID (shortId) for cursor positioning
     * @param offset - Offset from reference position (negative = before, positive = after)
     * @param limit - Maximum number of items to fetch
     * @returns Promise resolving to fetch result with values and boundary flags
     */
    public async fetchValues(
        referenceValue: string,
        offset: number,
        limit: number
    ): Promise<FetchResult<string>> {
        // Counted for the boards' loading bar, including the 404 fallback request.
        const endLoad = this.loadTracker.begin();
        try {
            return await this.fetchValuesUncounted(referenceValue, offset, limit);
        } finally {
            endLoad();
        }
    }

    /** The request(s) of {@link fetchValues}, without the load counter. */
    private async fetchValuesUncounted(
        referenceValue: string,
        offset: number,
        limit: number
    ): Promise<FetchResult<string>> {
        // Map empty string to null (AxisDataService<string> Interface gibt string vor)
        const referenceUid = referenceValue === '' ? null : referenceValue;

        const fetchRequest = {
            entityType: this.options.entityType,
            referenceUid,
            offset,
            limit,
            sort: this.options.sort,
            ...(this.options.queryParams?.length ? { queryParams: this.options.queryParams } : {})
        };

        let response;
        try {
            response = await this.axisDataClient.fetchAxisData(fetchRequest);
        } catch (error: unknown) {
            // 404 = deleted entity, retry from position 0
            if (error instanceof Error && error.message.includes('status 404')) {
                console.warn('[AxisData] 404 detected - retrying with referenceUid null (start from first entity)');
                response = await this.axisDataClient.fetchAxisData({ ...fetchRequest, referenceUid: null });
            } else {
                throw error;
            }
        }

        // Cache boundaries from response for subsequent getBoundaries calls
        this.cachedBoundaries = {
            min: response.boundaries.min,
            max: response.boundaries.max
        };

        // Extract shortIds and store metadata (title, color) for header providers
        const values = response.values.map(([, shortId, title, color]) => {
            axisEntityMetadata.set(shortId, { title, color });
            return shortId;
        });

        // Fire onFirstFetch callback once per service instance (only if non-empty response)
        if (!this._hasCalledFirstFetch && this.options.onFirstFetch && values.length > 0) {
            this._hasCalledFirstFetch = true;
            this.options.onFirstFetch(values[0]);
        }

        return {
            values,
            hasMoreBefore: response.hasMoreBefore,
            hasMoreAfter: response.hasMoreAfter,
            boundaries: response.boundaries
        };
    }

    /**
     * Retrieves the boundaries for the axis relative to the reference value.
     *
     * Uses cached boundaries if available from a previous fetchValues call.
     * Otherwise, performs a minimal fetch to obtain boundary information.
     *
     * @param referenceValue - The reference UID (shortId) for boundary calculation
     * @returns Promise resolving to axis boundaries (min/max relative indices)
     */
    public async getBoundaries(referenceValue: string): Promise<AxisBoundaries> {
        // Return cached boundaries if available
        if (this.cachedBoundaries !== null) {
            return this.cachedBoundaries;
        }

        // Fetch with minimal limit to obtain boundaries
        await this.fetchValues(referenceValue, 0, 1);

        // Boundaries should now be cached
        if (this.cachedBoundaries === null) {
            throw new Error('Failed to obtain axis boundaries from backend');
        }

        return this.cachedBoundaries;
    }

    /**
     * Expands the axis to include a missing value by fetching entities
     * between the current boundary and the missing entity.
     *
     * @param loadedBoundaryValues - Currently loaded [minShortId, maxShortId] boundary values
     * @param missingValue - ShortId of the missing value to include
     * @returns Promise resolving to expand result with values, insertion index, and direction
     */
    public async expandToValue(
        loadedBoundaryValues: [string, string],
        missingValue: string
    ): Promise<ExpandResult<string>> {
        // Counted for the boards' loading bar.
        const endLoad = this.loadTracker.begin();
        try {
            return await this.expandToValueUncounted(loadedBoundaryValues, missingValue);
        } finally {
            endLoad();
        }
    }

    /** The request of {@link expandToValue}, without the load counter. */
    private async expandToValueUncounted(
        loadedBoundaryValues: [string, string],
        missingValue: string
    ): Promise<ExpandResult<string>> {
        const response = await this.axisDataClient.expandToValue({
            entityType: this.options.entityType,
            minBoundaryUid: loadedBoundaryValues[0],
            maxBoundaryUid: loadedBoundaryValues[1],
            missingUid: missingValue,
            sort: this.options.sort,
            ...(this.options.queryParams?.length ? { queryParams: this.options.queryParams } : {})
        });

        // Extract shortIds and store metadata (title, color) for header providers
        const values = response.values.map(([, shortId, title, color]) => {
            axisEntityMetadata.set(shortId, { title, color });
            return shortId;
        });

        return {
            values,
            insertAtIndex: response.insertAtIndex,
            direction: response.direction
        };
    }

    /**
     * Clears the cached boundaries.
     * Call this when the underlying data may have changed.
     */
    public invalidateCache(): void {
        this.cachedBoundaries = null;
    }
}
